import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout/AppLayout';
import { useUser } from '../../context/UserContext';
import { useAuthorsList } from '../Authors/useAuthorsList';
import {
  buildDisplayLanguages,
  useBookLanguagesForForm,
} from '../Settings/lists/bookLanguageFormSupport';
import {
  settingsListApiError,
  type AgeGroupRow,
  type AuthorRow,
  type CategoryRow,
} from '../Settings/lists/singleTableListSupport';
import { useAgeGroupList } from '../Settings/lists/useAgeGroupList';
import { useCategoryList } from '../Settings/lists/useCategoryList';
import {
  apiBookToFormDraft,
  fetchBook,
  prepareBookDraftForApiUpload,
  updateBook,
} from './bookApi';
import BookForm, {
  bookDraftToUpdateApiPayload,
  bookSaveSuccessTitleAuthorLine,
  localizedTextEnMnThenAny,
  validateBookFormDraftDetailed,
  type BookFormDraft,
} from './BookForm';
import BookPreview from './BookPreview';
import BookSaveFullscreenLoading from './BookSaveFullscreenLoading';

function normalizeWriterEmailUi(raw: string): string {
  return raw.trim().toLowerCase();
}

function writerHasBookAccess(writerEmail: string | undefined, writerEmails: string[] | undefined): boolean {
  const norm = normalizeWriterEmailUi(writerEmail ?? '');
  if (!norm) return false;
  for (const e of writerEmails ?? []) {
    if (normalizeWriterEmailUi(String(e)) === norm) return true;
  }
  return false;
}

export default function BookEditPage() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const isManager = user?.role === 'MANAGER';
  const decodedId = bookId ? decodeURIComponent(bookId) : '';

  const [draft, setDraft] = useState<BookFormDraft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingBook, setLoadingBook] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookPreviewOpen, setBookPreviewOpen] = useState(false);
  const [validationErrorStep, setValidationErrorStep] = useState<number | null>(null);

  const { rows: fetchedBookLangs } = useBookLanguagesForForm(true);
  const bookFormLanguages = useMemo(() => buildDisplayLanguages(fetchedBookLangs), [fetchedBookLangs]);

  const { rows: authors, loading: authorsLoading, error: authorsError } = useAuthorsList<AuthorRow>();
  const { rows: categories, loading: categoriesLoading, error: categoriesError } =
    useCategoryList<CategoryRow>();
  const { rows: ageGroups, loading: ageGroupsLoading, error: ageGroupsError } =
    useAgeGroupList<AgeGroupRow>();

  const referenceLoading = authorsLoading || categoriesLoading || ageGroupsLoading;
  const referenceError = authorsError ?? categoriesError ?? ageGroupsError;

  const loadBook = useCallback(async () => {
    if (!decodedId.trim()) {
      setLoadError('Номын ID алга.');
      setDraft(null);
      setLoadingBook(false);
      return;
    }
    setLoadingBook(true);
    setLoadError(null);
    try {
      const book = await fetchBook(decodedId);
      setDraft(apiBookToFormDraft(book));
    } catch (e: unknown) {
      setLoadError(settingsListApiError(e, 'Ном ачааллахад алдаа гарлаа.'));
      setDraft(null);
    } finally {
      setLoadingBook(false);
    }
  }, [decodedId]);

  useEffect(() => {
    void loadBook();
  }, [loadBook]);

  /** WRITER may only edit books that list their email in `writer_emails` (aligned with API). */
  useEffect(() => {
    if (loadingBook || loadError) return;
    if (!user || user.role !== 'WRITER' || !draft || draft.mode !== 'edit') return;
    if (writerHasBookAccess(user.email, draft.writer_emails)) return;
    setDraft(null);
    setLoadError('Танд энэ номыг засварлах эрх алга. Зөвхөн таны имэйлийг бичээчдийн жагсаалтад оруулсан номыг засварлана.');
  }, [loadingBook, loadError, user, draft]);

  const handleDraftChange = useCallback((next: BookFormDraft) => {
    setValidationErrorStep(null);
    setDraft(next);
  }, []);

  const headingTitle = useMemo(() => {
    if (!draft) return 'Ном засварлах';
    const t = localizedTextEnMnThenAny(draft.name_map);
    return t || 'Ном засварлах';
  }, [draft]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft || draft.mode !== 'edit') return;
    setFormError(null);
    setSaveSuccess(null);
    setValidationErrorStep(null);

    if (user?.role === 'WRITER' && !writerHasBookAccess(user.email, draft.writer_emails)) {
      setFormError('Танд энэ номыг засварлах эрх алга. Зөвхөн таны имэйлийг бичээчдийн жагсаалтад оруулсан номыг засварлана.');
      return;
    }

      const validationFail = validateBookFormDraftDetailed(draft, {
        displayBookLanguages: bookFormLanguages,
      });
    if (validationFail) {
      setFormError(validationFail.message);
      setValidationErrorStep(validationFail.stepIndex);
      return;
    }
    setSubmitting(true);
    try {
      const withPaths = await prepareBookDraftForApiUpload(draft);
      const payload = bookDraftToUpdateApiPayload(withPaths, { omitWriterEmails: !isManager });
      await updateBook(payload);
      setSaveSuccess(bookSaveSuccessTitleAuthorLine(withPaths, authors));
      setValidationErrorStep(null);
      setDraft(withPaths);
    } catch (err: unknown) {
      setFormError(settingsListApiError(err, 'Ном шинэчлэхэд алдаа гарлаа.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <BookSaveFullscreenLoading open={submitting} />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          flex: 1,
          py: 4,
          px: { xs: 2, sm: 4 },
          mx: 'auto',
          width: 1,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
          <Button component={RouterLink} to="/library" variant="text" color="inherit" size="small">
            ← Номын сан
          </Button>
          {draft ? (
            <Tooltip title="Урьдчилан харах">
              <Button
                type="button"
                variant="text"
                color="primary"
                size="small"
                startIcon={<MenuBookOutlined />}
                onClick={() => setBookPreviewOpen(true)}
                aria-label="Урьдчилан харах"
                sx={{ textTransform: 'none', minWidth: 0 }}
              >
                Урьдчилан харах
              </Button>
            </Tooltip>
          ) : null}
        </Stack>

        {draft ? (
          <BookPreview
            open={bookPreviewOpen}
            onClose={() => setBookPreviewOpen(false)}
            draft={draft}
            authors={authors}
            languages={bookFormLanguages}
          />
        ) : null}

        <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
          {headingTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
          {decodedId ? `ID: ${decodedId}` : '—'}
        </Typography>

        {loadError ? (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={() => void loadBook()}>
              Дахин оролдох
            </Button>
          }>
            {loadError}
          </Alert>
        ) : null}

        {loadingBook ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label="Ном ачааллаж байна" />
          </Box>
        ) : null}

        {!loadingBook && draft ? (
          <>
            {formError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            ) : null}

            {saveSuccess != null ? (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>
                Хадгалагдлаа{saveSuccess ? `: ${saveSuccess}` : ''}
              </Alert>
            ) : null}

            <BookForm
              draft={draft}
              onChange={handleDraftChange}
              languages={bookFormLanguages}
              authors={authors}
              categories={categories}
              ageGroups={ageGroups}
              referenceLoading={referenceLoading}
              referenceError={referenceError}
              hideBookPreviewChrome
              validationErrorStep={validationErrorStep}
              isManager={isManager}
            />

            {formError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            ) : null}

            {saveSuccess != null ? (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSaveSuccess(null)}>
                Хадгалагдлаа{saveSuccess ? `: ${saveSuccess}` : ''}
              </Alert>
            ) : null}

            <Stack sx={{ mt: 3, width: 1 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={referenceLoading || submitting}
              >
                {submitting ? 'Хадгалж байна…' : 'Өөрчлөлт хадгалах'}
              </Button>
            </Stack>
          </>
        ) : null}

        {!loadingBook && !draft && !loadError ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Ном ачаалагдаагүй байна.
            <Button sx={{ ml: 1 }} onClick={() => navigate('/library')}>
              Жагсаалт руу
            </Button>
          </Alert>
        ) : null}
      </Box>
    </AppLayout>
  );
}
